import React, { useState, useCallback, useRef } from 'react';
import QRCode from 'react-qr-code';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
    FormControlLabel, Switch, Typography, Box, Divider, Tooltip, IconButton,
    InputAdornment
} from '@mui/material';
import { ContentCopy, Download, Visibility, VisibilityOff } from '@mui/icons-material';

// ── WiFi QR string helpers ────────────────────────────────────────────────────
// Format: WIFI:T:<auth>;S:<ssid>;P:<password>;H:<hidden>;;
// Ref: https://en.wikipedia.org/wiki/QR_code#Joining_a_Wi-Fi_network

function escapeWifiValue(value: string): string {
    // Backslash-escape special characters per the WiFi QR spec
    return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/"/g, '\\"');
}

function buildWifiString(ssid: string, security: string, password: string, hidden: boolean): string {
    if (!ssid.trim()) return '';
    const s = escapeWifiValue(ssid);
    const p = escapeWifiValue(password);
    const t = security === 'open' ? 'nopass' : security;
    const pw = security !== 'open' ? `P:${p};` : '';
    const h = hidden ? 'H:true;' : '';
    return `WIFI:T:${t};S:${s};${pw}${h};`;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface WifiSetupModalProps {
    onClose: () => void;
}

const WifiSetupModal: React.FC<WifiSetupModalProps> = ({ onClose }) => {
    const [ssid, setSsid] = useState('');
    const [security, setSecurity] = useState('WPA');
    const [password, setPassword] = useState('');
    const [hidden, setHidden] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);

    const qrRef = useRef<HTMLDivElement>(null);

    const wifiString = buildWifiString(ssid, security, password, hidden);
    const isValid = ssid.trim().length > 0 && (security === 'open' || password.length > 0);

    const handleSecurityChange = useCallback((e: SelectChangeEvent) => {
        setSecurity(e.target.value);
        if (e.target.value === 'open') setPassword('');
    }, []);

    const handleCopy = useCallback(async () => {
        if (!wifiString) return;
        await navigator.clipboard.writeText(wifiString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [wifiString]);

    const handleDownload = useCallback(() => {
        if (!qrRef.current || !isValid) return;
        const svg = qrRef.current.querySelector('svg');
        if (!svg) return;

        // Serialize SVG → Blob → object URL → <a> download
        const serializer = new XMLSerializer();
        const svgStr = serializer.serializeToString(svg);
        const blob = new Blob([svgStr], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wifi-${ssid.replace(/\s+/g, '_')}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    }, [isValid, ssid]);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>WiFi Network Setup</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Scan this QR code on the device to connect to the WiFi network automatically.
                </Typography>

                {/* ── Form ── */}
                <TextField
                    autoFocus
                    label="Network Name (SSID)"
                    value={ssid}
                    onChange={e => setSsid(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    required
                />

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Security</InputLabel>
                    <Select value={security} label="Security" onChange={handleSecurityChange}>
                        <MenuItem value="WPA">WPA / WPA2 / WPA3</MenuItem>
                        <MenuItem value="WEP">WEP</MenuItem>
                        <MenuItem value="open">None (open network)</MenuItem>
                    </Select>
                </FormControl>

                {security !== 'open' && (
                    <TextField
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{ mb: 2 }}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(v => !v)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                )}

                <FormControlLabel
                    control={
                        <Switch
                            checked={hidden}
                            onChange={e => setHidden(e.target.checked)}
                            size="small"
                        />
                    }
                    label="Hidden network"
                    sx={{ mb: 2 }}
                />

                <Divider sx={{ mb: 2 }} />

                {/* ── QR Code ── */}
                {isValid ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Box
                            ref={qrRef}
                            sx={{
                                p: 2,
                                bgcolor: '#fff',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'inline-block'
                            }}
                        >
                            <QRCode value={wifiString} size={220} />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Tooltip title={copied ? 'Copied!' : 'Copy WiFi string'}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ContentCopy />}
                                    onClick={handleCopy}
                                >
                                    {copied ? 'Copied' : 'Copy string'}
                                </Button>
                            </Tooltip>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={handleDownload}
                            >
                                Download SVG
                            </Button>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                            Click the configure Wifi from the mobile app and point it at the QR code.
                            The app will prompt to join <strong>{ssid}</strong> automatically.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: 240,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            color: 'text.disabled'
                        }}
                    >
                        <Typography variant="body2">
                            {ssid.trim() ? 'Enter a password to generate the QR code' : 'Enter a network name to generate the QR code'}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default WifiSetupModal;
